export type AreaKey =
  | "revenue" | "margin" | "cash" | "cost" | "inventory" | "procurement"
  | "supply" | "assets" | "people" | "customer" | "risk" | "esg";

export const areas: { key: AreaKey; label: [string, string] }[] = [
  { key: "revenue", label: ["Revenue", "Growth"] },
  { key: "margin", label: ["Margin", "Improvement"] },
  { key: "cash", label: ["Cash & Working", "Capital"] },
  { key: "cost", label: ["Cost", "Optimisation"] },
  { key: "inventory", label: ["Inventory &", "Demand"] },
  { key: "procurement", label: ["Procurement", "& Spend"] },
  { key: "supply", label: ["Supply Chain", "& Logistics"] },
  { key: "assets", label: ["Assets &", "Maintenance"] },
  { key: "people", label: ["People &", "Productivity"] },
  { key: "customer", label: ["Customer &", "Channel"] },
  { key: "risk", label: ["Risk, Compliance", "& Governance"] },
  { key: "esg", label: ["Sustainability", "& ESG"] },
];

export type Problem = {
  id: number;
  title: string;
  desc: string;
  area: AreaKey;
  tags: [string, string];
};

const P = (id: number, title: string, desc: string, area: AreaKey, t1: string, t2: string): Problem => ({
  id, title, desc, area, tags: [t1, t2],
});

export const problems: Problem[] = [
  P(1, "Rising Procurement Costs", "How can we reduce spend and improve supplier performance?", "procurement", "Procurement", "Cost"),
  P(2, "Low Sales Productivity", "How can we empower sales teams with data, AI and better insights?", "revenue", "Revenue", "People"),
  P(3, "High Inventory Levels", "How can we optimise inventory across plants and channels?", "inventory", "Supply Chain", "Inventory"),
  P(4, "Manual Month-End Process", "How can we close faster with better data and fewer errors?", "cash", "Finance", "Process"),
  P(5, "Customer Churn", "How can we improve customer retention and lifetime value?", "customer", "Customer", "Revenue"),
  P(6, "Asset Downtime", "How can we reduce unplanned downtime and improve asset utilisation?", "assets", "Assets", "Maintenance"),
  P(7, "Slow Order-to-Cash", "How can we turn orders into cash faster and with less friction?", "cash", "Finance", "Receivables"),
  P(8, "Margin Leakage", "How can we find and stop the quiet leaks that erode margin?", "margin", "Margin", "Pricing"),
  P(9, "Poor Supplier Performance", "How can we measure and improve supplier delivery and quality?", "procurement", "Procurement", "Supplier"),
  P(10, "Stock-outs on Key Products", "How can we keep the right products available at the right time?", "inventory", "Inventory", "Service"),
  P(11, "Late Deliveries", "How can we meet delivery promises across the network?", "supply", "Logistics", "Service"),
  P(12, "Demand Forecasting Is Inaccurate", "How can we improve forecasts to reduce stock-outs and excess inventory?", "inventory", "Planning", "Inventory"),
  P(13, "Unprofitable Customers", "How can we see which customers create or destroy margin?", "margin", "Customer", "Margin"),
  P(14, "Discount Overuse", "How can we control discounting without losing the deal?", "margin", "Pricing", "Sales"),
  P(15, "Cash Trapped in Receivables", "How can we release cash locked in overdue invoices?", "cash", "Finance", "Credit"),
  P(16, "Excess Working Capital", "How can we free working capital across payables, stock and receivables?", "cash", "Finance", "Liquidity"),
  P(17, "Rising Logistics Costs", "How can we reduce freight and distribution cost per unit?", "supply", "Logistics", "Cost"),
  P(18, "Fragmented Spend Visibility", "How can we see all spend in one place and act on it?", "procurement", "Procurement", "Data"),
  P(19, "Maintenance Spend Overruns", "How can we shift from reactive repairs to planned maintenance?", "assets", "Assets", "Cost"),
  P(20, "High Employee Attrition", "How can we understand and reduce why people leave?", "people", "People", "Retention"),
  P(21, "Slow Hiring", "How can we shorten time-to-hire without lowering the bar?", "people", "People", "Talent"),
  P(22, "Low Workforce Productivity", "How can we help teams do more of the work that matters?", "people", "People", "Process"),
  P(23, "Skill Gaps", "How can we map skills and close gaps before they hurt delivery?", "people", "People", "Skills"),
  P(24, "Weak Sales Pipeline", "How can we build a pipeline we can actually forecast?", "revenue", "Revenue", "Pipeline"),
  P(25, "Poor Cross-sell and Upsell", "How can we grow account value with smarter recommendations?", "revenue", "Revenue", "Customer"),
  P(26, "Channel Conflict", "How can we align distributors, retail and direct channels?", "customer", "Channel", "Pricing"),
  P(27, "Slow New Product Launch", "How can we bring new products to market faster?", "revenue", "Revenue", "Innovation"),
  P(28, "Inaccurate Product Costing", "How can we cost products so decisions rest on real numbers?", "margin", "Margin", "Costing"),
  P(29, "Overhead Cost Creep", "How can we keep indirect costs in check as we grow?", "cost", "Cost", "Finance"),
  P(30, "Duplicate Processes Across Units", "How can we standardise processes across business units?", "cost", "Process", "Cost"),
  P(31, "Manual Reporting Burden", "How can we replace spreadsheets with trusted, live reporting?", "cost", "Reporting", "Data"),
  P(32, "Slow Budget and Planning Cycle", "How can we plan faster and re-forecast with confidence?", "cost", "Planning", "Finance"),
  P(33, "Obsolete and Slow-moving Stock", "How can we spot and clear stock before it loses value?", "inventory", "Inventory", "Working Capital"),
  P(34, "Warehouse Inefficiency", "How can we speed up picking, packing and dispatch?", "supply", "Warehouse", "Process"),
  P(35, "Poor Supply Chain Visibility", "How can we track orders and risks end to end?", "supply", "Supply Chain", "Visibility"),
  P(36, "Supplier Concentration Risk", "How can we reduce dependence on a few critical suppliers?", "risk", "Risk", "Supplier"),
  P(37, "Compliance Failures", "How can we build compliance into the process, not around it?", "risk", "Compliance", "Process"),
  P(38, "Repeat Audit Findings", "How can we close audit findings for good?", "risk", "Governance", "Audit"),
  P(39, "Fraud and Leakage", "How can we detect fraud and leakage earlier?", "risk", "Risk", "Controls"),
  P(40, "Poor Data Quality", "How can we trust the data behind every decision?", "risk", "Governance", "Data"),
  P(41, "Access and Cyber Risk", "How can we secure access across SAP and connected systems?", "risk", "Security", "Access"),
  P(42, "Carbon Footprint Reporting", "How can we measure and report emissions with confidence?", "esg", "ESG", "Reporting"),
  P(43, "Energy Cost Volatility", "How can we manage energy use and cost more predictably?", "esg", "Energy", "Cost"),
  P(44, "Waste and Scrap", "How can we cut waste in production and logistics?", "esg", "Sustainability", "Operations"),
  P(45, "Sustainable Sourcing", "How can we source responsibly without raising cost?", "esg", "ESG", "Procurement"),
  P(46, "Unplanned Plant Shutdowns", "How can we predict failures before the line stops?", "assets", "Plant", "Maintenance"),
  P(47, "Poor Asset Utilisation", "How can we get more output from the assets we already own?", "assets", "Assets", "Utilisation"),
  P(48, "Long Customer Response Times", "How can we answer customers faster across every channel?", "customer", "Customer", "Service"),
  P(49, "Low Customer Satisfaction", "How can we find and fix what frustrates customers most?", "customer", "Customer", "Experience"),
  P(50, "Inconsistent Customer Data", "How can we build one trusted view of every customer?", "customer", "Customer", "Data"),
  P(51, "Disconnected Systems", "How can we connect systems so teams work from one version of truth?", "cost", "Systems", "Integration"),
  P(52, "AI Pilots Not Scaling", "How can we move AI from pilots to measurable business value?", "cost", "AI", "Value"),
];

export const slugify = (s: string) =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export type Featured = {
  id: number;
  title: string;
  question: string;
  highlights: string[];
  outcomes: [string, string][];
  image: string;
};

export const featured: Featured[] = [
  {
    id: 12,
    title: "Demand Forecasting is Inaccurate",
    question:
      "How can we improve demand forecasting to reduce stock-outs, excess inventory and working capital using SAP, data and AI?",
    highlights: [
      "Integrate sales, market and supply data",
      "Use SAP IBP and predictive AI models",
      "Improve forecast accuracy by 20–30%",
      "Reduce inventory and working capital",
      "Enable real-time collaboration across functions",
    ],
    outcomes: [["30%", "Better forecast accuracy"], ["20%", "Lower inventory"], ["15%", "Higher service levels"]],
    image: "/assets/images/business-problems/featured-demand.jpg",
  },
  {
    id: 1,
    title: "Rising Procurement Costs",
    question:
      "How can we reduce spend, consolidate suppliers and improve supplier performance using SAP Ariba, data and AI?",
    highlights: [
      "Bring all spend into one view",
      "Use SAP Ariba for sourcing and contracts",
      "Benchmark suppliers on price and quality",
      "Cut maverick and off-contract buying",
      "Automate approvals and purchase-to-pay",
    ],
    outcomes: [["8%", "Lower addressable spend"], ["25%", "Faster PO cycle"], ["12%", "Fewer off-contract buys"]],
    image: "/assets/images/business-problems/featured-procurement.jpg",
  },
  {
    id: 5,
    title: "Customer Churn",
    question:
      "How can we spot at-risk customers early and improve retention and lifetime value using SAP, data and AI?",
    highlights: [
      "Unify customer, order and service data",
      "Score churn risk with predictive models",
      "Trigger retention plays automatically",
      "Link service issues to revenue impact",
      "Track lifetime value by segment",
    ],
    outcomes: [["18%", "Lower churn"], ["10%", "Higher repeat revenue"], ["2x", "Faster issue response"]],
    image: "/assets/images/business-problems/featured-churn.jpg",
  },
];

export const outcomes = [
  { name: "Haldiram's", text: "Improved demand planning and inventory efficiency.", metrics: ["20% lower inventory", "15% faster closing"], logo: "../../../assets/images/haldiram.png" },
  { name: "Zydus Wellness", text: "AI-enabled sales & COGS planning platform.", metrics: ["30% better forecast accuracy"], logo: "../../../assets/images/zydus.png" },
  { name: "DS Group", text: "S/4HANA migration with 9 verticals and 5-year AMS.", metrics: ["Higher process efficiency"], logo: "../../../assets/images/ds-group-1.png" },
  { name: "ECG", text: "Supply chain visibility and operational control.", metrics: ["Improved asset utilisation"], logo: "../../../assets/images/ECGs.png" },
];
